import { useQuery } from "@tanstack/react-query";
import { dashboardAPI, adminAPI } from "../../utils/api";
import {
  Package,
  Users,
  Truck,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Activity,
  Loader2,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { useMemo, useCallback } from "react";

// ─── Helpers ───────────────────────────────────────────────────────
const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  if (isNaN(num)) return "0.00";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatNumber = (val) => {
  const num =
    typeof val === "string"
      ? parseInt(val, 10)
      : typeof val === "number"
        ? val
        : 0;
  if (isNaN(num)) return "0";
  return num.toLocaleString("en-US");
};

const formatPercent = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  if (isNaN(num)) return "0";
  return num.toFixed(1);
};

const formatHours = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  if (isNaN(num)) return "0";
  return num.toFixed(1);
};

// ─── Components ────────────────────────────────────────────────────
const StatCard = ({
  title,
  value,
  icon: Icon,
  trend,
  trendValue,
  color = "blue",
  isLoading,
}) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-emerald-50 text-emerald-600 border-emerald-100",
    yellow: "bg-amber-50 text-amber-600 border-amber-100",
    red: "bg-rose-50 text-rose-600 border-rose-100",
    purple: "bg-violet-50 text-violet-600 border-violet-100",
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-100",
  };

  const iconColors = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-emerald-100 text-emerald-600",
    yellow: "bg-amber-100 text-amber-600",
    red: "bg-rose-100 text-rose-600",
    purple: "bg-violet-100 text-violet-600",
    cyan: "bg-cyan-100 text-cyan-600",
  };

  return (
    <div
      className={`card border ${colors[color]} hover:shadow-md transition-shadow duration-200`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          {isLoading ? (
            <div className="mt-2 h-8 flex items-center">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <p className="text-2xl font-bold text-gray-900 mt-2 truncate">
              {value}
            </p>
          )}
          {trend && !isLoading && (
            <div
              className={`flex items-center mt-2 text-sm font-medium ${
                trend === "up" ? "text-emerald-600" : "text-rose-600"
              }`}
            >
              {trend === "up" ? (
                <TrendingUp className="w-4 h-4 mr-1 flex-shrink-0" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1 flex-shrink-0" />
              )}
              <span className="truncate">{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl flex-shrink-0 ${iconColors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="card animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-8 bg-gray-200 rounded w-16 mt-3"></div>
        <div className="h-4 bg-gray-200 rounded w-20 mt-2"></div>
      </div>
      <div className="h-12 w-12 bg-gray-200 rounded-xl"></div>
    </div>
  </div>
);

const EmptyChart = ({ title }) => (
  <div className="card flex flex-col items-center justify-center h-[300px]">
    <Activity className="w-12 h-12 text-gray-300 mb-3" />
    <p className="text-gray-500 font-medium">{title}</p>
    <p className="text-sm text-gray-400 mt-1">No data available</p>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100 text-sm">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-gray-600">
          <span
            className="inline-block w-2 h-2 rounded-full mr-2"
            style={{ backgroundColor: entry.color }}
          ></span>
          {entry.value?.toLocaleString?.() || entry.value}
        </p>
      ))}
    </div>
  );
};

// ─── Main Dashboard ────────────────────────────────────────────────
const AdminDashboard = () => {
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery({
    queryKey: ["adminStats"],
    queryFn: () => adminAPI.stats.overview(),
    refetchInterval: 30000,
    retry: 2,
  });

  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ["adminChart"],
    queryFn: () => dashboardAPI.chartData(30),
    retry: 2,
  });

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ["adminActivity"],
    queryFn: () => dashboardAPI.recentActivity(10),
    retry: 2,
  });

  // Extract data with fallbacks
  const stats = useMemo(() => statsData?.data?.data ?? {}, [statsData]);
  const chart = useMemo(() => chartData?.data?.data ?? {}, [chartData]);
  const activity = useMemo(
    () => activityData?.data?.data ?? [],
    [activityData],
  );

  // Status colors mapping
  const statusColors = useMemo(
    () => ({
      pending: "#f59e0b",
      assigned: "#3b82f6",
      picked_up: "#8b5cf6",
      in_transit: "#06b6d4",
      delivered: "#10b981",
      failed: "#ef4444",
      cancelled: "#6b7280",
    }),
    [],
  );

  // Memoized status data for pie chart
  const statusData = useMemo(
    () =>
      chart.status_breakdown?.map((item) => ({
        name: item.status,
        value: item.count,
        color: statusColors[item.status] || "#6b7280",
      })) ?? [],
    [chart.status_breakdown, statusColors],
  );

  // Activity icon helper
  const getActivityIcon = useCallback((status) => {
    switch (status) {
      case "delivered":
        return <CheckCircle className="w-4 h-4" />;
      case "failed":
        return <XCircle className="w-4 h-4" />;
      case "cancelled":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  }, []);

  const getActivityColors = useCallback((status) => {
    switch (status) {
      case "delivered":
        return "bg-emerald-100 text-emerald-600";
      case "failed":
        return "bg-rose-100 text-rose-600";
      case "cancelled":
        return "bg-gray-100 text-gray-600";
      default:
        return "bg-blue-100 text-blue-600";
    }
  }, []);

  // Error state
  if (statsError) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">
            Failed to load dashboard
          </h3>
          <p className="text-gray-500 mt-2">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Real-time overview of your courier operations
          </p>
        </div>
        <div className="text-sm text-gray-400">Auto-refreshes every 30s</div>
      </div>

      {/* Primary Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <StatCard
              title="Total Orders"
              value={formatNumber(stats.total_orders)}
              icon={Package}
              color="blue"
              trend="up"
              trendValue={`+${formatNumber(stats.today_orders)} today`}
            />
            <StatCard
              title="Total Revenue"
              value={`EGP ${formatMoney(stats.total_revenue)}`}
              icon={DollarSign}
              color="green"
              trend="up"
              trendValue={`EGP ${formatMoney(stats.today_revenue)} today`}
            />
            <StatCard
              title="Active Couriers"
              value={formatNumber(stats.active_couriers)}
              icon={Truck}
              color="yellow"
            />
            <StatCard
              title="Total Customers"
              value={formatNumber(stats.total_customers)}
              icon={Users}
              color="purple"
              trend="up"
              trendValue={`+${formatNumber(stats.new_customers_this_week)} this week`}
            />
          </>
        )}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <StatCard
              title="Pending Orders"
              value={formatNumber(stats.pending_orders)}
              icon={Clock}
              color="yellow"
            />
            <StatCard
              title="Delivered"
              value={formatNumber(stats.delivered_orders)}
              icon={CheckCircle}
              color="green"
            />
            <StatCard
              title="Completion Rate"
              value={`${formatPercent(stats.completion_rate)}%`}
              icon={Activity}
              color="blue"
            />
            <StatCard
              title="Avg Delivery Time"
              value={`${formatHours(stats.avg_delivery_time)}h`}
              icon={Clock}
              color="cyan"
            />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Package className="w-5 h-5 mr-2 text-blue-500" />
            Orders (Last 30 Days)
          </h3>
          {chartLoading ? (
            <div className="h-[300px] flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : chart.orders_per_day && chart.orders_per_day.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chart.orders_per_day}>
                <defs>
                  <linearGradient
                    id="ordersGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#ordersGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart title="No orders data available" />
          )}
        </div>

        {/* Revenue Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-emerald-500" />
            Revenue (Last 30 Days)
          </h3>
          {chartLoading ? (
            <div className="h-[300px] flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : chart.revenue_per_day && chart.revenue_per_day.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chart.revenue_per_day}>
                <defs>
                  <linearGradient
                    id="revenueGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `EGP${value}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart title="No revenue data available" />
          )}
        </div>
      </div>

      {/* Status Breakdown & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Pie Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-violet-500" />
            Order Status
          </h3>
          {chartLoading ? (
            <div className="h-[250px] flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : statusData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} orders`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {statusData.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center text-sm p-2 rounded-lg bg-gray-50"
                  >
                    <div
                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="capitalize text-gray-600 truncate">
                      {item.name}
                    </span>
                    <span className="ml-auto font-semibold text-gray-900">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyChart title="No status data available" />
          )}
        </div>

        {/* Recent Activity */}
        <div className="card lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-blue-500" />
            Recent Activity
          </h3>
          {activityLoading ? (
            <div className="h-[250px] flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : activity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Activity className="w-12 h-12 mb-3 text-gray-300" />
              <p className="font-medium">No recent activity</p>
              <p className="text-sm mt-1">Activities will appear here</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {activity.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
                >
                  <div
                    className={`p-2.5 rounded-lg flex-shrink-0 ${getActivityColors(item.status)}`}
                  >
                    {getActivityIcon(item.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
